import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("admin_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("admin_token");
      if (window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin") {
        window.location.href = "/admin";
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) =>
  apiClient.post("/auth/login", { email, password });

export const register = (email, password) =>
  apiClient.post("/auth/register", { email, password });

export const getMe = () => apiClient.get("/auth/me");

// Releases
export const getReleases = () => apiClient.get("/releases");
export const getRelease = (id) => apiClient.get(`/releases/${id}`);
export const createRelease = (data) => apiClient.post("/releases", data);
export const updateRelease = (id, data) => apiClient.put(`/releases/${id}`, data);
export const deleteRelease = (id) => apiClient.delete(`/releases/${id}`);

// DSP Services
export const getDSPServices = () => apiClient.get("/dsp-services");
export const createDSPService = (data) => apiClient.post("/dsp-services", data);
export const deleteDSPService = (id) => apiClient.delete(`/dsp-services/${id}`);

// Social Platforms
export const getSocialPlatforms = () => apiClient.get("/social-platforms");
export const createSocialPlatform = (data) => apiClient.post("/social-platforms", data);
export const deleteSocialPlatform = (id) => apiClient.delete(`/social-platforms/${id}`);

// Seed defaults
export const seedDefaults = () => apiClient.post("/seed-defaults");

// Fetch links from Odesli/Songlink
export const fetchLinksFromUrl = (url) => apiClient.post("/fetch-links", { url });

// Tribe integration
export const getTribeArtists = () => apiClient.get("/tribe/artists");
export const getTribeArtist = (artistId) => apiClient.get(`/tribe/artists/${artistId}`);
export const getHiddenArtists = () => apiClient.get("/tribe/hidden-artists");
export const hideManagementArtist = (artistId, artistName) => apiClient.post("/tribe/hide-artist", { artist_id: artistId, artist_name: artistName });
export const showManagementArtist = (artistId) => apiClient.post("/tribe/show-artist", { artist_id: artistId });

// News
export const getNews = () => apiClient.get("/news");
export const searchArtistNews = () => apiClient.get("/news/search");
export const createNews = (data) => apiClient.post("/news", data);
export const deleteNews = (id) => apiClient.delete(`/news/${id}`);

// Promotion Clients
export const getPromotionClients = () => apiClient.get("/promotion-clients/list");
export const addPromotionClient = (name) => apiClient.post("/promotion-clients/add", { name });
export const deletePromotionClient = (id) => apiClient.delete(`/promotion-clients/${id}`);
export const togglePromotionClientFeatured = (id, featured) => apiClient.put(`/promotion-clients/${id}/featured`, { featured });

// Shop / Products
export const getProducts = () => apiClient.get("/products");
export const getProduct = (id) => apiClient.get(`/products/${id}`);
export const createProduct = (data) => apiClient.post("/products", data);
export const updateProduct = (id, data) => apiClient.put(`/products/${id}`, data);
export const deleteProduct = (id) => apiClient.delete(`/products/${id}`);
export const seedProducts = () => apiClient.post("/seed-products");

// Shipping
export const calculateShipping = (country) => apiClient.get(`/shipping/calculate?country=${country}`);

// Checkout
export const createCheckoutSession = (items, shippingAddress, originUrl) => 
  apiClient.post("/checkout/create-session", { items, shipping_address: shippingAddress, origin_url: originUrl });
export const getCheckoutStatus = (sessionId) => apiClient.get(`/checkout/status/${sessionId}`);

// Orders (admin)
export const getOrders = () => apiClient.get("/orders");
export const getOrder = (id) => apiClient.get(`/orders/${id}`);
export const updateOrderStatus = (id, status) => apiClient.put(`/orders/${id}/status`, { status });

// Promo Artist Images (manual overrides)
export const getPromoArtistImages = () => apiClient.get("/promo-artist-images");
export const setPromoArtistImage = (name, imageUrl) => apiClient.post("/promo-artist-image", { name, image_url: imageUrl });
export const deletePromoArtistImage = (name) => apiClient.delete(`/promo-artist-image/${encodeURIComponent(name)}`);

// Upload promo artist image file
export const uploadPromoArtistImage = async (name, file) => {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('file', file);
  return apiClient.post("/promo-artist-image/upload", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export default apiClient;
